import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, isRef as _isRef, vModelText as _vModelText, withDirectives as _withDirectives, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "mb-2" }
const _hoisted_2 = { class: "flex flex-col items-center justify-start w-full sm:space-x-4 sm:flex-row" }
const _hoisted_3 = { class: "flex flex-col py-5 space-y-5 sm:w-full sm:justify-start sm:space-y-0 sm:py-0" }
const _hoisted_4 = { class: "text-3xl font-bold sm:text-left" }
const _hoisted_5 = { class: "pt-2 break-words" }
const _hoisted_6 = {
  key: 0,
  class: "flex items-center justify-between my-5 ml-2"
}
const _hoisted_7 = {
  key: 0,
  class: "flex gap-x-1"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "font-bold" }, "Join Code:", -1)
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { class: "flex items-center justify-center gap-1" }
const _hoisted_11 = /*#__PURE__*/_createTextVNode(" Make Predictions ")
const _hoisted_12 = /*#__PURE__*/_createTextVNode(" View Predictions ")
const _hoisted_13 = /*#__PURE__*/_createElementVNode("span", null, "Join", -1)
const _hoisted_14 = {
  key: 3,
  class: "flex"
}
const _hoisted_15 = { class: "px-3 py-1 text-white rounded-l bg-primary" }
const _hoisted_16 = /*#__PURE__*/_createTextVNode(" Copy ")
const _hoisted_17 = /*#__PURE__*/_createTextVNode(" Get QR Code ")
const _hoisted_18 = /*#__PURE__*/_createElementVNode("span", null, "Leave", -1)
const _hoisted_19 = {
  key: 1,
  class: "flex items-center justify-between my-5 ml-2"
}
const _hoisted_20 = /*#__PURE__*/_createElementVNode("span", null, "Join", -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("span", null, " To join a faction, you must have an invite link or an invite code. ", -1)

import { Faction, FactionPrivacy } from '@/typings/faction';
import { computed, inject, Ref, ref } from 'vue';
import router from '@/router';
import FactionImage from '@/components/Faction/FactionImage.vue';
import { apiFactions } from '@/api/faction';
import { PromptModal, AlertModal } from '@/typings/modal';
import Modal from '@/components/Modal/Modal.vue';
import TextField from '@/components/Inputs/TextField.vue';
import { useFactionsStore } from '@/store/factions';
import { ScorecardStatus } from '@/typings/scorecard';

interface Props {
  faction: Faction;
  singleView: boolean;
  founderView: boolean;
}


export default /*#__PURE__*/_defineComponent({
  props: {
    faction: null,
    singleView: { type: Boolean },
    founderView: { type: Boolean }
  },
  emits: ["updated"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'updated'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  faction: Faction;
  singleView: boolean;
  founderView: boolean;
}



const factionStore = useFactionsStore();

const joiningModalVisible = ref(false);
const joiningCode = ref('');
const joiningError = ref('');

const canBeJoined = computed(() => {
  if (props.faction.Joined) return false;

  if (props.faction.FactionCards?.length) {
    const card = props.faction.FactionCards[0];
    return new Date(card.LocksAt) > new Date();
  }
  return !props.faction.Joined;
});

const cardAvailable = computed(() => {
  if (props.faction.FactionCards?.length) {
    const card = props.faction.FactionCards[0];
    return card.Status !== ScorecardStatus.Scheduled;
  }
  return false;
});

const cardViewOnly = computed(() => {
  if (props.faction.FactionCards?.length) {
    const card = props.faction.FactionCards[0];
    return [
      ScorecardStatus.Locked,
      ScorecardStatus.Finalizing,
      ScorecardStatus.Closed,
    ].includes(card.Status);
  }
  return false;
});

function playCard() {
  if (props.faction.FactionCards?.length) {
    const card = props.faction.FactionCards[0];
    router.push(`/card/${card.Slug}`);
  }
}

async function downloadQrCode() {
  if (!props.faction) return;

  const response = await apiFactions.downloadQrCode(props.faction);
  const type = response.headers['content-type'];
  const fileName = response.headers['content-disposition']
    .split('filename=')[1]
    .split(';')[0];
  const blob = new Blob([response.data], { type });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
}

const inviteLink = computed(
  () =>
    `${location.host}/#/invite/f/${props.faction.Uuid}/${props.faction.JoinCode}`
);

function attemptCopy() {
  navigator.clipboard.writeText(inviteLink.value).then(() => {
    alert('Invite link copied to clipboard');
  });
}

async function join() {
  if (props.faction.Privacy === FactionPrivacy.Public) {
    await factionStore.joinPublic(props.faction);
  } else {
    joiningModalVisible.value = true;
  }
}

const promptModal = inject('promptModal') as Ref<PromptModal>;
const alertModal = inject('alertModal') as Ref<AlertModal>;

async function leave() {
  if (props.faction) {
    const doLeave = await promptModal.value.show(
      'Leave Faction?',
      'You are about to leave this faction. You will be removed from all leaderboards and will lose all brand reigns. Once you have left, you will need to join again to begin competing with other members. Are you sure you want to leave?'
    );
    if (doLeave) {
      await factionStore.leave(props.faction);
      alertModal.value.show('Faction Left', 'You have left the faction.');
    }
  }
}

async function joinFaction() {
  if (joiningCode.value.trim()) {
    let success = false;
    if (props.faction.Privacy === FactionPrivacy.InviteOnly) {
      success = await factionStore.joinInviteOnly(
        props.faction,
        joiningCode.value
      );
    } else if (
      props.faction &&
      props.faction.Privacy === FactionPrivacy.Public
    ) {
      success = await factionStore.joinPublic(props.faction);
    } else {
      success = await factionStore.joinPrivate(joiningCode.value);
    }

    if (success) {
      joiningModalVisible.value = false;
      joiningCode.value = '';
      emit('updated');
    } else {
      joiningError.value = 'This code might not be valid!';
    }
  } else {
    joiningError.value = 'Cannot be empty!';
  }
}

return (_ctx: any,_cache: any) => {
  const _component_fa_icon = _resolveComponent("fa-icon")!
  const _component_Button = _resolveComponent("Button")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", null, [
          _createVNode(FactionImage, {
            faction: __props.faction,
            class: "w-40 h-40"
          }, null, 8, ["faction"])
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, _toDisplayString(__props.faction.Name), 1),
          _createElementVNode("div", _hoisted_5, _toDisplayString(__props.faction.Description), 1)
        ])
      ]),
      (__props.faction.Joined)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (__props.faction.Privacy !== _unref(FactionPrivacy).Public && !__props.singleView)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _hoisted_8,
                  _createElementVNode("div", null, _toDisplayString(__props.faction.JoinCode), 1)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_9)),
            _createElementVNode("div", _hoisted_10, [
              (__props.singleView && _unref(cardAvailable) && !_unref(cardViewOnly))
                ? (_openBlock(), _createBlock(_component_Button, {
                    key: 0,
                    onClick: playCard
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_fa_icon, {
                        icon: "play",
                        class: "mr-2"
                      }),
                      _hoisted_11
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (__props.singleView && _unref(cardViewOnly))
                ? (_openBlock(), _createBlock(_component_Button, {
                    key: 1,
                    onClick: playCard
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_fa_icon, {
                        icon: "eye",
                        class: "mr-2"
                      }),
                      _hoisted_12
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (_unref(canBeJoined))
                ? (_openBlock(), _createBlock(_component_Button, {
                    key: 2,
                    onClick: join,
                    class: "py-1"
                  }, {
                    default: _withCtx(() => [
                      _hoisted_13
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (__props.founderView)
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                    _createElementVNode("div", _hoisted_15, [
                      _createVNode(_component_fa_icon, { icon: "envelope" })
                    ]),
                    _withDirectives(_createElementVNode("input", {
                      class: "px-2 border-t border-b border-primary",
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(inviteLink) ? (inviteLink).value = $event : null)),
                      readonly: ""
                    }, null, 512), [
                      [_vModelText, _unref(inviteLink)]
                    ]),
                    _createVNode(_component_Button, {
                      class: "rounded-l-none rounded-r",
                      onClick: attemptCopy
                    }, {
                      default: _withCtx(() => [
                        _hoisted_16
                      ]),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true),
              (__props.founderView)
                ? (_openBlock(), _createBlock(_component_Button, {
                    key: 4,
                    onClick: downloadQrCode
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_fa_icon, {
                        icon: "qrcode",
                        class: "mr-2"
                      }),
                      _hoisted_17
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (!__props.founderView)
                ? (_openBlock(), _createBlock(_component_Button, {
                    key: 5,
                    onClick: leave
                  }, {
                    default: _withCtx(() => [
                      _hoisted_18
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_19, [
            (_unref(canBeJoined))
              ? (_openBlock(), _createBlock(_component_Button, {
                  key: 0,
                  onClick: join,
                  class: "py-1"
                }, {
                  default: _withCtx(() => [
                    _hoisted_20
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]))
    ]),
    (joiningModalVisible.value)
      ? (_openBlock(), _createBlock(Modal, {
          key: 0,
          header: "Join a Faction",
          onClose: _cache[3] || (_cache[3] = ($event: any) => (joiningModalVisible.value = false))
        }, {
          footer: _withCtx(() => [
            _createVNode(_component_Button, {
              label: "Join",
              onClick: joinFaction
            }),
            _createVNode(_component_Button, {
              label: "Cancel",
              type: "secondary",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (joiningModalVisible.value = false))
            })
          ]),
          default: _withCtx(() => [
            _hoisted_21,
            _createVNode(TextField, {
              modelValue: joiningCode.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((joiningCode).value = $event)),
              "validation-error": joiningError.value,
              title: "Code"
            }, null, 8, ["modelValue", "validation-error"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})